import { instanceToPlain } from 'class-transformer';
import getPlaceType from '../helpers/mapbox/getPlaceType';
import { MapboxPlaceObject } from './mapbox/MapboxPlaceObject';
import { Coordinate } from './Coordinate';
import { PlaceType } from './PlaceType';
import { MultiPolygonGeoJsonFeature } from './PolygonGeoJsonFeature';
import { GeographicArea } from './GeographicArea';
import turfBbox from '@turf/bbox';
import { BoundedRectangle } from './BoundedRectangle';
import getBoundedRectangleFromBbox from '../helpers/boundedRectangles/getBoundedRectangleFromBbox';

export type PlaceObject = {
  type: PlaceType;
  name: string;
  bbox?: Array<number>;
  geoJsonFeature?: MultiPolygonGeoJsonFeature;
  areaId?: string;
  center: Coordinate;
  contextArea?: string;
};

export class Place {
  type: PlaceType;
  name: string;
  bbox?: Array<number>;
  geoJsonFeature?: MultiPolygonGeoJsonFeature; // TODO: should this eventually not be optional?
  areaId?: string;
  center: Coordinate;
  contextArea?: string;

  static fromMapboxPlaceObject(mapboxPlaceObject: MapboxPlaceObject) {
    return Place.fromObject({
      type: getPlaceType(mapboxPlaceObject),
      name: mapboxPlaceObject.place_name,
      center: mapboxPlaceObject.center,
      bbox: mapboxPlaceObject.bbox,
      // TODO: create the geoJsonFeature representation from mapbox's bbox
    });
  }

  static fromObject(placeObject: PlaceObject) {
    return new Place(placeObject);
  }

  constructor(placeObject: PlaceObject) {
    this.type = placeObject.type;
    this.name = placeObject.name;
    this.bbox = placeObject.bbox;
    this.geoJsonFeature = placeObject.geoJsonFeature;
    this.areaId = placeObject.areaId;
    this.center = placeObject.center;
    this.contextArea = placeObject.contextArea;
  }

  isAddress() {
    return this.type === PlaceType.Address;
  }

  isNeighborhood() {
    return this.type === PlaceType.Neighborhood;
  }

  isArea() {
    return (
      this.type === PlaceType.Region ||
      this.type === PlaceType.County ||
      this.type === PlaceType.District ||
      this.type === PlaceType.City ||
      this.type === PlaceType.Village ||
      this.type === PlaceType.Municipality ||
      this.type === PlaceType.Town ||
      this.type === PlaceType.Locality ||
      this.type === PlaceType.Neighborhood ||
      this.type === PlaceType.Postcode ||
      this.type === PlaceType.Intersection
    );
  }

  isPostcode() {
    return this.type === PlaceType.Postcode;
  }

  isOtherPlace() {
    return this.type === PlaceType.Place;
  }

  shortName() {
    return this.name.split(', ')[0];
  }

  // This function will create a lowercased string of the address's first two words plus the city
  deduplicationName() {
    const addressFirst2Words = this.shortName()
      .split(' ')
      .slice(0, 2)
      .join(' ');

    const city = this.name.split(', ')[1].split(' ').slice(0, 1).join(' ');

    return `${addressFirst2Words}, ${city}`.toLowerCase();
  }

  generalArea() {
    return this.name.split(', ').slice(1).join(', ');
  }

  addressWithPotentialUnitNumber(unitNumber?: string) {
    if (this.type !== PlaceType.Address || !unitNumber) {
      return this.name;
    }

    return `${unitNumber}-${this.name}`;
  }

  toObject() {
    return instanceToPlain(this);
  }

  toGeographicArea(): GeographicArea {
    const boundingBox: BoundedRectangle = getBoundedRectangleFromBbox(
      this.bbox || turfBbox(this.geoJsonFeature),
    );
    const geoArea: GeographicArea = {
      name: this.name,
      type: this.type,
      contextArea: this.contextArea || '',
      boundingBox,
      geoJsonFeature: this.geoJsonFeature,
      areaId: this.areaId,
    };
    return geoArea;
  }
}
